import { AppSettings } from "@/AppSettings";
import { useMemo } from "react";
import { ConfigurationType } from ".";
import { ConfigurationSourceDataForm, ConfigurationSourceDataMap, Metadata, SourceDataFormStatus, SourceDataPriority, Trial } from "@/api/dtos";


const excludedConfigurationSourceDataFormCopyProperties: Partial<Record<keyof ConfigurationSourceDataForm, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    caseReportFormOid: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataFormSettings = (
    source: ConfigurationSourceDataForm,
    target: ConfigurationSourceDataForm,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataForm;

        if (!excludedConfigurationSourceDataFormCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createFormConfiguration = (
    metadata?: Metadata, 
    trialConfigurationForms?: ConfigurationSourceDataMap['forms'], 
    configurationForms?: ConfigurationSourceDataMap['forms'], 
    trial?: Trial,
    isGlobal?: boolean, 
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    baseRoute?: string
) => {
    if (!metadata || !metadata.forms || !caseReportFormOid) {
        return undefined;
    }

    const formMetadata = metadata.forms?.[caseReportFormOid];

    if (!formMetadata) {
        return undefined;
    }

    const eventFormMetadata = studyEventOid ? metadata?.eventForms?.[studyEventOid]?.[caseReportFormOid] : undefined;


    if (studyEventOid && !eventFormMetadata) {
        return undefined;
    }


    const formConfiguration = configurationForms?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid];
    const eventFormConfiguration = studyEventOid ? configurationForms?.[studyEventOid]?.[caseReportFormOid] : undefined;

    const trialFormConfiguration = trialConfigurationForms?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid];
    const trialEventFormConfiguration = studyEventOid ? trialConfigurationForms?.[studyEventOid]?.[caseReportFormOid] : undefined;


    const dominantConfiguration = eventFormConfiguration?.linked === false ? 
        eventFormConfiguration : 
        trialEventFormConfiguration?.linked === false ?
            trialEventFormConfiguration :
            formConfiguration?.linked === false ?
                formConfiguration :
                trialFormConfiguration

    const parentRoute = baseRoute ?
        baseRoute :
        `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}`;

    const configurationLink = studyEventOid ? 
        `${parentRoute}/events/${studyEventOid}/forms/${formMetadata?.caseReportFormOid}` :
        `${parentRoute}/forms/${formMetadata?.caseReportFormOid}`

    return {
        formMetadata: formMetadata,
        eventFormMetadata: eventFormMetadata,
        formConfiguration: formConfiguration,
        eventFormConfiguration: eventFormConfiguration,
        trialFormConfiguration: trialFormConfiguration,
        trialEventFormConfiguration: trialEventFormConfiguration,
        configuration: dominantConfiguration,

        linked: studyEventOid ?
            eventFormConfiguration?.linked !== false :
            formConfiguration?.linked !== false,

        type: eventFormConfiguration?.linked === false ?
            ConfigurationType.Event :
            trialEventFormConfiguration?.linked === false ?
                ConfigurationType.TrialEvent :
                formConfiguration?.linked === false ?
                    ConfigurationType.Form :
                    ConfigurationType.Trial,

        icon: AppSettings.icons.form,

        studyEventOid: studyEventOid,
        caseReportFormOid: formMetadata.caseReportFormOid,

        displayName: dominantConfiguration?.name ?? formMetadata?.name,

        priority: dominantConfiguration?.priority,

        configurationLink: configurationLink,
        configurationFormVersionsLink: `${configurationLink}/versions`
    };
}

const useFormConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial, 
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    baseRoute?: string
) => {
    return useMemo(() => {
        return createFormConfiguration(
            metadata, 
            trialConfiguration?.forms, 
            configuration?.forms, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId, 
            studyEventOid,
            caseReportFormOid,
            baseRoute
        );
    }, [metadata, trialConfiguration?.forms, configuration?.forms, studyEventOid, caseReportFormOid, trial, configurationSourceDataId, baseRoute]);
};

export default useFormConfiguration;